import {
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle
} from 'react'

import IMask, { InputMask } from 'imask'
import Input from '@/components/common/Input/Input'

interface DecimalInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  onChange?: (value: string) => void
  label?: string
  error?: string
  className?: string
  disabled?: boolean
  placeholder?: string
  postfix?: string
  scale?: number
  padFractionalZeros?: boolean
  normalizeZeros?: boolean
  radix?: string
  mapToRadix?: string[]
  min?: number
  max?: number
  suffix?: React.ReactNode
  optional?: boolean
  optionalText?: string
}

export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(
  ({
    onChange,
    defaultValue,
    scale = 2,
    padFractionalZeros = true,
    normalizeZeros = true,
    radix = '.',
    mapToRadix = [','],
    min = 0,
    max,
    postfix = '',
    ...props
  }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const inputMask = useRef<InputMask>(undefined)

    useImperativeHandle(ref, () => inputRef.current!, [])

    useEffect(() => {
      if (!inputRef.current) return

      inputMask.current = IMask(
        inputRef.current,
        {
          mask: `num${postfix}`,
          eager: true,
          blocks: {
            num: {
              mask: Number,
              scale,
              thousandsSeparator: ' ',
              padFractionalZeros,
              normalizeZeros,
              radix,
              mapToRadix,
              min,
              max,
            }
          }
        }
      )

      if (defaultValue) {
        inputMask.current.value = defaultValue.toString()
      }

      inputMask.current.on("complete", () => {
        const numericValue = inputMask.current?.unmaskedValue
        onChange?.(numericValue || min.toString())
      })

      return () => {
        inputMask.current?.destroy()
      }
    }, [scale, padFractionalZeros, normalizeZeros, radix, min, max, postfix])

    useEffect(() => {
      if (inputMask.current && defaultValue) {
        inputMask.current.value = defaultValue.toString()
        inputMask.current.updateValue()
      }
    }, [defaultValue])

    return (
      <Input
        ref={inputRef}
        {...props}
      />
    )
  }
)

DecimalInput.displayName = 'DecimalInput'

export default DecimalInput